// My Ads widget: data-365="my-ads"
// The signed-in advertiser's ad placements at THIS venue — status, monthly price, and the actions an
// ADVERTISER owns: Cancel (their subscription), Update card (Stripe billing portal), and Pay now /
// Manage (first-party redirect for approved-not-paid + anything creative). Pause/Resume are the VENUE's
// controls (Admin), so they're intentionally absent here. Login-gated read, same model as member-card:
// reads GET /api/venue-ads/my-ads then the per-ad placements, filtered to cfg.venueId.
// Changing the ad itself (Edit / Fix & resubmit) is a round trip to the 365 builder (uploads, video
// conversion, live preview), which brings the advertiser straight back here afterwards.
import { Api } from '../api';
import { Auth, AuthRequiredError } from '../auth';
import type { EmbedConfig, ThemeOptions } from '../types';
import { createRoot, h, money, footer, clear, renderLoading, signOutRow, showSalesPaused, blockIfSalesPaused } from '../ui';
import { sitePage, hasMount } from '../pages';
import { track, trackIds } from '../analytics';

interface MyAd {
  id: string;
  title?: string | null;
  businessName?: string | null;
  contentType?: string | number | null; // StaticImage / RichTextCard / Video / QrCoupon (or the enum number)
  mediaUrl?: string | null;
  cardImageUrl?: string | null;
  businessLogoUrl?: string | null;
  headline?: string | null;
  offerText?: string | null;
}
interface Placement {
  id: string;
  venueId?: string | null;
  status?: string | null;          // PendingApproval / Rejected / Approved / PaymentPending / Active / Paused / Cancelled / Expired
  monthlyPrice?: number | null;
  paymentFailedAt?: string | null;
  rejectionReason?: string | null;
  submittedAt?: string | null;
  cancelledReason?: string | null;         // set when the VENUE removed the ad
  scanCount?: number | null;               // lifetime human QR scans
  impressionCount?: number | null;         // lifetime times shown on the venue's screens
  currentMonthImpressions?: number | null; // this calendar month (server resets monthly)
}
interface AdItem {
  ad: MyAd;
  pl: Placement;
  locked: boolean;     // a venue (any venue) approved this ad, so it can't change any more - fixes go into a copy
  superseded: boolean; // a newer placement for this ad at this venue exists (it was already resubmitted)
}

function platformBase(cfg: EmbedConfig): string {
  return (cfg.loginUrl || '').replace(/\/embed-login\.html.*$/, '').replace(/\/$/, '');
}

export function mountMyAds(mount: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, theme: ThemeOptions): void {
  const { root } = createRoot(mount, theme);
  if (cfg.advertisingEnabled) showSalesPaused(root, cfg);
  const pending = auth.peekPending<{ t?: string }>();
  if (pending && pending.t === 'my-ads') auth.clearPending();
  void render(root, cfg, api, auth);
}

async function render(root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth): Promise<void> {
  clear(root);
  root.append(h('h3', undefined, 'My Ads'));

  if (!auth.isLoggedIn) {
    root.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 10px' }, 'Sign in to view and manage the ads you run here.'));
    const login = h('button', { class: 'e365-btn' }, 'Sign in') as HTMLButtonElement;
    login.addEventListener('click', () => auth.redirectToLogin({ t: 'my-ads' }));
    root.append(login, footer());
    return;
  }

  const host = h('div');
  root.append(host);
  renderLoading(host, 'My Ads');

  let ads: MyAd[];
  try {
    ads = await api.get<MyAd[]>('/api/venue-ads/my-ads');
  } catch (e) {
    clear(host);
    if (e instanceof AuthRequiredError) {
      host.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 10px' }, 'Please sign in again to view your ads.'));
      const login = h('button', { class: 'e365-btn' }, 'Sign in') as HTMLButtonElement;
      login.addEventListener('click', () => auth.redirectToLogin({ t: 'my-ads' }));
      host.append(login, footer());
    } else {
      host.append(h('div', { class: 'e365-alert error' }, 'We couldn’t load your ads. Please try again.'));
      const retry = h('button', { class: 'e365-btn' }, 'Try again') as HTMLButtonElement;
      retry.addEventListener('click', () => void render(root, cfg, api, auth));
      host.append(retry, footer());
    }
    return;
  }

  // Fetch each ad's placements and keep the ones for THIS venue. Track failures so an outage
  // doesn't masquerade as "you haven't advertised here yet."
  const venueId = (cfg.venueId || '').toLowerCase();
  const lists = await Promise.all((ads || []).map((ad) =>
    api.get<Placement[]>(`/api/venue-ads/ad/${ad.id}/placements`)
      .then((pls) => ({ ad, pls: pls || [] }))
      .catch(() => ({ ad, pls: null as Placement[] | null }))));
  const anyFailed = lists.some((x) => x.pls === null);
  const items: AdItem[] = [];
  let elsewhere = false;
  for (const { ad, pls } of lists) {
    const all = pls || [];
    const locked = all.some((p) => p.status === 'Approved' || p.status === 'PaymentPending' || p.status === 'Active');
    const here = all.filter((p) => (p.venueId || '').toLowerCase() === venueId);
    if (all.length > here.length) elsewhere = true;
    for (const pl of here) {
      const t = Date.parse(pl.submittedAt || '') || 0;
      const superseded = here.some((o) => o.id !== pl.id && (Date.parse(o.submittedAt || '') || 0) > t);
      items.push({ ad, pl, locked, superseded });
    }
  }

  clear(host);
  if (items.length === 0) {
    if (anyFailed) {
      host.append(h('div', { class: 'e365-alert error' }, 'We couldn’t load your ads right now. Please try again.'));
      const retry = h('button', { class: 'e365-btn' }, 'Try again') as HTMLButtonElement;
      retry.addEventListener('click', () => void render(root, cfg, api, auth));
      host.append(retry, footer());
      return;
    }
    host.append(
      hasMount('advertise')
        ? h('div', { class: 'e365-alert info' }, 'You haven’t advertised here yet. Create an ad above to reach everyone at this venue.')
        : h('div', { class: 'e365-alert info' }, 'You haven’t advertised here yet. ',
            h('a', { href: sitePage('advertise') }, 'Create an ad'), ' to reach everyone at this venue.'),
      signOutRow(auth.profile?.email, () => { auth.logout(); void render(root, cfg, api, auth); }),
      footer(),
    );
    return;
  }
  if (anyFailed) host.append(h('div', { class: 'e365-alert warn' }, 'Some of your ads couldn’t be loaded right now — refresh to retry.'));

  for (const it of items) host.append(renderAdCard(it, cfg, api, auth, root));
  host.append(
    // Only for advertisers who also run ads at other venues - everything for THIS venue is managed above.
    elsewhere
      ? h('a', { href: `${platformBase(cfg)}/my-ads`, class: 'e365-muted', style: 'display:inline-block;margin-top:4px;font-size:.8rem;' }, 'Your ads at other venues →')
      : '',
    signOutRow(auth.profile?.email, () => { auth.logout(); void render(root, cfg, api, auth); }),
    footer(),
  );
}

// Per-status label, badge color, and explanatory line.
function statusView(pl: Placement, screensComingSoon = false): { label: string; bg: string; line: string; lineClass: string } {
  const s = pl.status || '';
  // The venue is still installing its TVs: approved ads wait, and paying opens with the first screen (API 2026-11-03).
  if (screensComingSoon && (s === 'Approved' || s === 'PaymentPending')) {
    return { label: 'Approved', bg: '#0a7d33', line: 'Approved! The venue is still installing its screens. You can start your ad once they’re live.', lineClass: 'e365-alert info' };
  }
  switch (s) {
    case 'PendingApproval': return { label: 'Pending review', bg: '#b7791f', line: 'Waiting for the venue to approve your ad.', lineClass: 'e365-muted' };
    case 'Approved': return { label: 'Approved', bg: '#0a7d33', line: 'Approved! Start your subscription to go live.', lineClass: 'e365-alert info' };
    case 'PaymentPending': return { label: 'Payment pending', bg: '#b7791f', line: 'We’re processing your payment.', lineClass: 'e365-muted' };
    case 'Active': return pl.paymentFailedAt
      ? { label: 'Live', bg: '#0a7d33', line: 'Your last payment didn’t go through — update your card to keep your ad live.', lineClass: 'e365-alert error' }
      : { label: 'Live', bg: '#0a7d33', line: 'Your ad is live on the venue’s screens.', lineClass: 'e365-muted' };
    case 'Paused': return { label: 'Paused', bg: '#6b7280', line: 'The venue paused your ad. You’re not billed while it’s paused.', lineClass: 'e365-muted' };
    case 'Rejected': return { label: 'Not approved', bg: '#b23b00', line: pl.rejectionReason || 'The venue didn’t approve this ad.', lineClass: 'e365-alert error' };
    case 'Cancelled': return { label: 'Cancelled', bg: '#6b7280', line: pl.cancelledReason ? `The venue ended this ad: ${pl.cancelledReason}` : 'This ad has ended.', lineClass: 'e365-muted' };
    case 'Expired': return { label: 'Ended', bg: '#6b7280', line: 'This placement has ended.', lineClass: 'e365-muted' };
    default: return { label: s || 'Unknown', bg: '#6b7280', line: '', lineClass: 'e365-muted' };
  }
}

function renderAdCard(it: AdItem, cfg: EmbedConfig, api: Api, auth: Auth, root: HTMLElement): HTMLElement {
  const { ad, pl } = it;
  const v = statusView(pl, !!cfg.adScreensComingSoon);
  const card = h('div', {
    style: 'border:1px solid var(--e365-border); border-radius:var(--e365-radius); padding:14px; margin-bottom:10px; background:var(--e365-surface);',
  });

  const info = h('div', { style: 'flex:1; min-width:0;' });
  info.append(h('div', { style: 'display:flex; justify-content:space-between; align-items:center; gap:10px; margin-bottom:4px;' },
    h('div', { style: 'font-weight:700; font-size:1.02rem;' }, ad.title || 'Ad'),
    h('span', { style: `display:inline-block; font-size:.72rem; font-weight:700; padding:2px 8px; border-radius:999px; color:#fff; background:${v.bg};` }, v.label)));
  if (ad.businessName) info.append(h('div', { class: 'e365-muted', style: 'font-size:.8rem;' }, ad.businessName));
  if (pl.monthlyPrice != null) info.append(h('div', { class: 'e365-muted', style: 'font-size:.85rem;' }, `${money(pl.monthlyPrice)}/mo`));
  card.append(h('div', { style: 'display:flex; gap:12px; align-items:flex-start;' }, preview(ad), info));
  // The number advertisers pay for: times shown on this venue's screens (deduped server-side).
  if ((pl.impressionCount ?? 0) > 0 || pl.status === 'Active' || pl.status === 'Paused') {
    const month = pl.currentMonthImpressions ?? 0;
    card.append(h('div', { style: 'font-size:.85rem; margin-top:2px;' },
      `👁 ${(pl.impressionCount ?? 0).toLocaleString()} times shown`,
      h('span', { class: 'e365-muted' }, ` · ${month.toLocaleString()} this month`),
      (pl.scanCount ?? 0) > 0 ? h('span', undefined, ` · ${(pl.scanCount ?? 0).toLocaleString()} QR scans`) : ''));
  }
  if (v.line) card.append(h('div', { class: v.lineClass, style: 'margin-top:8px; font-size:.85rem;' }, v.line));

  const s = pl.status || '';
  const canCancel = ['PendingApproval', 'Approved', 'PaymentPending', 'Active', 'Paused'].includes(s);
  const canUpdateCard = s === 'Active' || s === 'Paused';
  const canPay = (s === 'Approved' || s === 'PaymentPending') && !cfg.adScreensComingSoon;

  const actions = h('div', { class: 'e365-slots', style: 'margin-top:10px;' });
  if (canPay) {
    const pay = h('button', { class: 'e365-btn', type: 'button', style: 'width:auto;margin-top:0;' }, 'Pay now') as HTMLButtonElement;
    pay.addEventListener('click', () => void payNow(pl, cfg, api, auth, pay));
    actions.append(pay);
  }
  if (canUpdateCard) {
    const upd = h('button', { class: s === 'Active' && pl.paymentFailedAt ? 'e365-btn' : 'e365-slot', type: 'button', style: s === 'Active' && pl.paymentFailedAt ? 'width:auto;margin-top:0;' : '' }, 'Update card') as HTMLButtonElement;
    upd.addEventListener('click', () => void openBillingPortal(pl, api, upd, auth));
    actions.append(upd);
  }
  // Full analytics (charts, best times, CSV) live on the 365 site - same page as the My Ads dashboard there.
  if ((pl.impressionCount ?? 0) > 0 || (pl.scanCount ?? 0) > 0 || s === 'Active' || s === 'Paused') {
    actions.append(h('a', { class: 'e365-slot', href: `${platformBase(cfg)}/my-ads/${ad.id}/analytics`, target: '_blank', rel: 'noopener' }, 'See analytics'));
  }
  const editUrl = (id: string, resubmit: boolean) =>
    `${platformBase(cfg)}/create-ad?edit=${encodeURIComponent(id)}${resubmit ? '&resubmit=true' : ''}` +
    `&embedVenue=${encodeURIComponent(cfg.venueId)}&return=${encodeURIComponent(location.href.split('?')[0])}`;
  if (s === 'Rejected' && !it.superseded) {
    // Fix it and send it back to this venue. If another venue already approved the ad, the fix goes into a copy.
    const fix = h('button', { class: 'e365-btn', type: 'button', style: 'width:auto;margin-top:0;' }, 'Fix & resubmit') as HTMLButtonElement;
    fix.addEventListener('click', () => void fixAndResubmit(it, api, auth, fix, editUrl));
    actions.append(fix);
  } else if (s === 'PendingApproval' && !it.locked) {
    actions.append(h('a', { class: 'e365-slot', href: editUrl(ad.id, false) }, 'Edit ad'));
  }
  if (canCancel) {
    const cancel = h('button', { class: 'e365-slot', type: 'button' }, 'Cancel ad') as HTMLButtonElement;
    cancel.addEventListener('click', () => void cancelPlacement(it, cfg, api, auth, cancel, root));
    actions.append(cancel);
  }
  if (actions.childNodes.length) card.append(actions);

  return card;
}

// A small picture of the ad: the image itself, or the card's headline / coupon offer on a dark tile.
function preview(ad: MyAd): HTMLElement {
  const box = 'width:96px; height:54px; flex:0 0 96px; border-radius:6px; overflow:hidden; background:#111; ' +
    'display:flex; align-items:center; justify-content:center; text-align:center;';
  const type = String(ad.contentType ?? '');
  const img = type === 'StaticImage' || type === '0' ? ad.mediaUrl : (ad.cardImageUrl || ad.businessLogoUrl);
  if (img && /^https:\/\//i.test(img)) {
    return h('div', { style: box }, h('img', { src: img, alt: '', loading: 'lazy', style: 'width:100%;height:100%;object-fit:cover;' }));
  }
  const text = type === 'Video' || type === '2' ? '▶ Video' : (ad.offerText || ad.headline || ad.businessName || 'Ad');
  return h('div', { style: box + 'color:#fff; font-weight:800; font-size:.66rem; line-height:1.15; padding:4px;' }, text.slice(0, 40));
}

async function fixAndResubmit(it: AdItem, api: Api, auth: Auth, btn: HTMLButtonElement,
  editUrl: (id: string, resubmit: boolean) => string): Promise<void> {
  if (!it.locked) { window.location.href = editUrl(it.ad.id, true); return; }
  btn.disabled = true;
  btn.textContent = 'Opening…';
  try {
    const copy = await api.post<{ id?: string }>(`/api/venue-ads/${it.ad.id}/copy`, {});
    if (copy && copy.id) { window.location.href = editUrl(copy.id, true); return; }
    throw new Error('no copy');
  } catch (e) {
    btn.disabled = false;
    btn.textContent = 'Fix & resubmit';
    if (e instanceof AuthRequiredError) { auth.redirectToLogin({ t: 'my-ads' }); return; }
    window.alert('We couldn’t open your ad for changes. Please try again.');
  }
}

// Start the placement's subscription checkout DIRECTLY (the endpoint accepts Approved and
// PaymentPending-retry). Falls back to the first-party /my-ads page only if the deep link fails —
// the advertiser should never have to re-find their ad just to pay for it.
async function payNow(pl: Placement, cfg: EmbedConfig, api: Api, auth: Auth, btn: HTMLButtonElement): Promise<void> {
  if (blockIfSalesPaused(cfg, btn)) return;
  btn.disabled = true;
  btn.textContent = 'Opening checkout…';
  try {
    // Funnel + attribution: the checkout body carries `wa` (optional; the API ignores it when absent).
    track('ad_checkout_start', 'my-ads');
    const wa = trackIds();
    const res = await api.post<{ checkoutUrl?: string }>(`/api/venue-ads/placement/${pl.id}/checkout`, wa ? { wa } : {});
    if (res && res.checkoutUrl) { window.location.href = res.checkoutUrl; return; }
    throw new Error('no checkout url');
  } catch (e) {
    if (e instanceof AuthRequiredError) { auth.redirectToLogin({ t: 'my-ads' }); return; }
    window.location.href = `${platformBase(cfg)}/my-ads`;
  }
}

async function openBillingPortal(pl: Placement, api: Api, btn: HTMLButtonElement, auth: Auth): Promise<void> {
  const original = btn.textContent || 'Update card';
  btn.disabled = true;
  btn.textContent = 'Opening…';
  try {
    const res = await api.post<{ portalUrl?: string }>(`/api/venue-ads/placement/${pl.id}/billing-portal`, {});
    if (res && res.portalUrl) { window.location.href = res.portalUrl; return; }
    throw new Error('no portal url');
  } catch (e) {
    btn.disabled = false;
    btn.textContent = original;
    if (e instanceof AuthRequiredError) { auth.redirectToLogin({ t: 'my-ads' }); return; }
    window.alert('We couldn’t open the payment update page. Please try again.');
  }
}

async function cancelPlacement(it: AdItem, cfg: EmbedConfig, api: Api, auth: Auth, btn: HTMLButtonElement, root: HTMLElement): Promise<void> {
  if (!window.confirm(`Cancel “${it.ad.title || 'this ad'}” at ${cfg.venueName}? It will stop showing and billing will end.`)) return;
  btn.disabled = true;
  btn.textContent = 'Cancelling…';
  try {
    await api.post(`/api/venue-ads/placement/${it.pl.id}/cancel`);
    void render(root, cfg, api, auth);
  } catch (e) {
    btn.disabled = false;
    btn.textContent = 'Cancel ad';
    if (e instanceof AuthRequiredError) { auth.redirectToLogin({ t: 'my-ads' }); return; }
    window.alert('We couldn’t cancel right now. Please try again.');
  }
}
